import { IEmployee } from './../types/employee'
import { toastMsg } from '../../../components/Toast/toastMsg'
import { ActionCreator } from 'redux'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { UserSettingsApi } from '../../../api/userSettingsApi'
import * as constants from './constants'
import { IDepartment } from '../types/department'
import { UserSettingsState } from '../reducers'
import { IPagination } from '../../../types/pagination'

export type DepartmentThunkResult<R> = ThunkAction<Promise<R>, UserSettingsState, null, DepartmentActions>
export type DepartmentThunkDispatchType = ThunkDispatch<UserSettingsState, null, DepartmentActions>


export interface UserSettingsHasErrored {
  type: constants.USER_SETTINGS_HAS_ERRORED
  hasErrored: boolean
}

export interface GetDepartmentsSuccess {
  type: constants.GET_DEPARTMENTS_SUCCESS
  departments: IDepartment[]
}

export interface GetPaginatedDepartmentsSuccess {
  type: constants.GET_PAGINATED_DEPARTMENTS_SUCCESS
  departments: IPagination<IDepartment[]>
}

export interface DeleteDepartmentSuccess {
  type: constants.DELETE_DEPARTMENT_SUCCESS
  department: IDepartment
}

export interface CreateDepartmentSuccess {
  type: constants.CREATE_DEPARTMENT_SUCCESS
  department: IDepartment
}

export interface UpdateDepartmentSuccess {
  type: constants.UPDATE_DEPARTMENT_SUCCESS
  department: IDepartment
}

export interface SortDepartments {
  type: constants.SORT_DEPARTMENTS,
  sort: { name: string, direction: string }
}

export interface AddEmployeesToDepartment {
  type: constants.ADD_EMPLOYEES_TO_DEPARTMENT
  employees: IEmployee[]
}

export interface GetDepartment {
  type: constants.GET_DEPARTMENT
  id: string | number
}

export interface GetDepartmentSuccess {
  type: constants.GET_DEPARTMENT_SUCCESS
  department: IDepartment
}

export interface SelectedDepartment {
  type: constants.SELECTED_DEPARTMENT
  department: IDepartment
}

export interface OpenDepartmentForm {
  type: constants.OPEN_DEPARTMENT_FORM,
  open: boolean
}

export interface SetDepartmentActivePage {
  type: constants.SET_DEPARTMENT_ACTIVE_PAGE
  page: number
}

export interface FilterDepartments {
  type: constants.FILTER_DEPARTMENTS,
  search: string
}

export const userSettingsHasErrored = (hasErrored: boolean, error: Error, action?: string): UserSettingsHasErrored => {
  if (action) {
    const errorMsg = error.message
    toastMsg(errorMsg, action, 'error')
  }
  return {
    type: constants.USER_SETTINGS_HAS_ERRORED,
    hasErrored: hasErrored
  }
}

export const getDepartments: ActionCreator<DepartmentThunkResult<IDepartment[]>>
  = (queryParams?: Record<string, string>): DepartmentThunkResult<IDepartment[]> => {
  return async (dispatch: DepartmentThunkDispatchType) => {
    return UserSettingsApi.getDepartments(queryParams)
      .then(response => {
        dispatch(getDepartmentsSuccess(response))
        return response
      })
      .catch(error => {
        dispatch(userSettingsHasErrored(true, error.response.data, constants.GET_DEPARTMENTS))
        return []
      })
  }
}

export const getDepartmentsSuccess = (departments: IDepartment[]): GetDepartmentsSuccess => {
  return {
    type: constants.GET_DEPARTMENTS_SUCCESS,
    departments
  }
}

export const getPaginatedDepartments: ActionCreator<DepartmentThunkResult<IPagination<IDepartment[]>>>
= (queryParams?: Record<string, string> ): DepartmentThunkResult<IPagination<IDepartment[]>> => {
  return async (dispatch: DepartmentThunkDispatchType) => {
    return UserSettingsApi.getPaginatedDepartments(queryParams)
    .then(response => {
      dispatch(getPaginatedDepartmentsSuccess(response))
      return response
    })
    .catch(error => {
      dispatch(userSettingsHasErrored(true, error.response.data, constants.GET_PAGINATED_DEPARTMENTS))
      return {} as IPagination<IDepartment[]>
    })
  }
}

export const getPaginatedDepartmentsSuccess = (departments: IPagination<IDepartment[]>): GetPaginatedDepartmentsSuccess => {
  return {
    type: constants.GET_PAGINATED_DEPARTMENTS_SUCCESS,
    departments
  }
}

export const getDepartment: ActionCreator<DepartmentThunkResult<IDepartment>>
  = (id: string | number): DepartmentThunkResult<IDepartment> => {
    return async (dispatch: DepartmentThunkDispatchType) => {
      return UserSettingsApi.getDepartment(id)
        .then(response => {
          dispatch(getDepartmentSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(userSettingsHasErrored(true, error.response.data, constants.GET_DEPARTMENT))
          return {} as IDepartment
        })
    }
  }

export const getDepartmentSuccess = (department: IDepartment): GetDepartmentSuccess => {
  return {
    type: constants.GET_DEPARTMENT_SUCCESS,
    department
  }
}

export const deleteDepartment: ActionCreator<DepartmentThunkResult<string | number | void>>
  = (department: IDepartment): DepartmentThunkResult<string | number | void> => {
  return async (dispatch: DepartmentThunkDispatchType) => {
    return UserSettingsApi.deleteDepartment(department.id)
      .then(response => {
        dispatch(deleteDepartmentSuccess(department))
        return response
      })
      .catch(error => {
        dispatch(userSettingsHasErrored(true, error.response.data, constants.DELETE_DEPARTMENT))
      })
  }
}

export const deleteDepartmentSuccess = (department: IDepartment): DeleteDepartmentSuccess => {
  toastMsg(`Deleted ${department.name} department successfully.`, constants.DELETE_DEPARTMENT_SUCCESS, 'success')
  return {
    type: constants.DELETE_DEPARTMENT_SUCCESS,
    department
  }
}

export const createDepartment: ActionCreator<
  DepartmentThunkResult<IDepartment>
> = (department: IDepartment): DepartmentThunkResult<IDepartment> => {
  return async (dispatch: DepartmentThunkDispatchType) => {
    return UserSettingsApi.createDepartment(department)
      .then(response => {
        dispatch(createDepartmentSuccess(response))
        return response
      })
      .catch(error => {
        dispatch(userSettingsHasErrored(true, error.response.data, constants.CREATE_DEPARTMENT))
        return {} as IDepartment
      })
  }
}

export const createDepartmentSuccess = (department: IDepartment): CreateDepartmentSuccess => {
  toastMsg(`Created ${department.name} department successfully.`, constants.CREATE_DEPARTMENT_SUCCESS, 'success')
  return {
    type: constants.CREATE_DEPARTMENT_SUCCESS,
    department
  }
}

export const updateDepartment: ActionCreator<
DepartmentThunkResult<IDepartment>
> = (department: IDepartment): DepartmentThunkResult<IDepartment>  => {
  return async (dispatch: DepartmentThunkDispatchType) => {
    return UserSettingsApi.updateDepartment(department)
      .then(response => {
        dispatch(updateDepartmentSuccess(response))
        return response
      })
      .catch(error => {
        dispatch(userSettingsHasErrored(true, error.response.data, constants.UPDATE_DEPARTMENT))
        return {} as IDepartment
      })
  }
}

export const updateDepartmentSuccess = (department: IDepartment): UpdateDepartmentSuccess => {
  toastMsg(`Updated ${department.name} department successfully.`, constants.UPDATE_DEPARTMENT_SUCCESS, 'success')
  return {
    type: constants.UPDATE_DEPARTMENT_SUCCESS,
    department
  }
}

export const sortDepartments = (name: string, direction: string): SortDepartments => {
  return {
    type: constants.SORT_DEPARTMENTS,
    sort: { name: name, direction: direction }
  }
}

export const addEmployeesToDepartment = (employees: IEmployee[]): AddEmployeesToDepartment => {
  return {
    type: constants.ADD_EMPLOYEES_TO_DEPARTMENT,
    employees
  }
}

export const selectedDepartment = (department: IDepartment): SelectedDepartment => {
  return {
    type: constants.SELECTED_DEPARTMENT,
    department
  }
}

export const openDepartmentForm = (open: boolean): OpenDepartmentForm => {
  return {
    type: constants.OPEN_DEPARTMENT_FORM,
    open: open
  }
}

export const setDepartmentActivePage = (page: number): SetDepartmentActivePage => {
  return {
    type: constants.SET_DEPARTMENT_ACTIVE_PAGE,
    page: page
  }
}

export const filterDepartments = (search: string): FilterDepartments => {
  return {
    type: constants.FILTER_DEPARTMENTS,
    search: search
  }
}

export type DepartmentActions
  = GetDepartmentsSuccess
  | GetPaginatedDepartmentsSuccess
  | GetDepartmentSuccess
  | DeleteDepartmentSuccess
  | CreateDepartmentSuccess
  | UpdateDepartmentSuccess
  | UserSettingsHasErrored
  | SortDepartments
  | AddEmployeesToDepartment
  | SelectedDepartment
  | OpenDepartmentForm
  | SetDepartmentActivePage
  | FilterDepartments
